import React from 'react';
import styled from 'styled-components';
import { Pergunta } from '../types';

interface QuestionRendererProps {
  pergunta: Pergunta;
  value: any;
  onChange: (value: any) => void;
  numeroDependentes?: number;
  usuariosQueAtendem?: string[];
  tipoUsuario?: string;
}

const ContainerQuestion = styled.div`
  margin: 25px 0;
  background: #ffffff;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 15px;
    margin: 15px 0;
    
    /* Força quebra de linha em todos os elementos de texto */
    * {
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      hyphens: auto !important;
    }
  }
`;

const OptionGroup = styled.div`
  display: flex;
  width: 100%;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    flex-direction: row;
    margin-top: 10px;
    gap: 5px;
  }
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;    
  border: 1px solid #eee;
  flex: 1;
  background: #fff;
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
  }
`;

const OptionTitle = styled.div`
  background: #222222;
  color: #fff;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px 8px 0 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  
  @media (max-width: 768px) {
    height: 35px;
    font-size: 12px;
    padding: 0 8px;
    text-align: center;
    word-break: break-word;
    line-height: 1.2;
  }
`;

const OptionRow = styled.div`
  height: 50px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  padding: 15px 20px;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  
  /* Texto à esquerda */
  > span, > div:first-child {
    flex: 1;
    text-align: left;
  }
  
  /* Inputs centralizados */
  > input[type="radio"], > input[type="checkbox"] {
    margin: 0 auto;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    height: 65px;
    margin-bottom: 10px;
    font-size: 11px;
    padding: 8px 6px;
    line-height: 1.2;
    word-break: break-word;
  }
`;

const AnswersContent = styled.div`
  display: flex;
  flex: 1;
  border-left: 1px solid #eee;
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
  }
`;

const Table = styled.table`
  margin: 20px 0;
  border: 1px solid #e9ecef;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  /* Força alinhamento à esquerda para primeira coluna */
  td:first-child {
    text-align: center !important;
  }
  
  @media (max-width: 768px) {
    margin: 10px 0;
    font-size: 12px;
    
    td:first-child {
      text-align: center !important;
    }
  }
`;

const Tr = styled.tr`
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TdCentered = styled.td`
  padding: 15px 20px;
  border-right: 1px solid #eee;
  font-size: 14px;
  color: #333;
  vertical-align: middle;
  text-align: center !important;
  max-width: 200px;
  min-width: 150px;
  
  @media (max-width: 768px) {
    max-width: 120px;
    min-width: 100px;
    font-size: 10px;
    padding: 6px 4px;
    text-align: center !important;
  }
`;

const Td = styled.td<{ inputType?: string }>`
  border-right: 1px solid #e9ecef;
  ${props => {
    if (props.inputType === "text") {
      return `padding: 12px 16px;`;
    } else {
      return `padding: 16px 20px;`;
    }
  }}
  font-size: 14px;
  color: #495057;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  
  &:last-child {
    border-right: none;
  }
  
  /* Primeira coluna (tipo de usuário) */
  &:first-child {
    text-align: center !important;
    max-width: 200px;
    min-width: 150px;
    padding: 15px 20px;
    
    @media (max-width: 768px) {
      max-width: 120px;
      min-width: 100px;
      font-size: 10px;
      padding: 6px 4px;
      text-align: center !important;
    }
  }
  
  /* Colunas de input (centralizadas) */
  &:not(:first-child) {
    text-align: center;
    vertical-align: middle;
    
    /* Inputs centralizados */
    input[type="radio"], input[type="checkbox"] {
      margin: 0 auto;
      display: block;
    }
  }
  
  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 11px;
    word-break: break-word;
    line-height: 1.2;
    
    /* Primeira coluna mantém alinhamento à esquerda no mobile */
    &:first-child {
      text-align: left;
    }
    
    /* Outras colunas centralizadas no mobile */
    &:not(:first-child) {
      text-align: center;
    }
  }
`;

const Thead = styled.thead`
  background: #222222;
  color: #fff;
  
  td {
    color: #fff;
    font-weight: 600;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }
  
  @media (max-width: 768px) {
    td {
      font-size: 11px;
      padding: 8px 6px;
      word-break: break-word;
      line-height: 1.2;
    }
  }
`;

const Tbody = styled.tbody`
  background: #ffffff;
`;

const Option = styled.input`
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  max-width: 22px;
  max-height: 22px;
  cursor: pointer;
  accent-color: #222222;
  margin-right: 12px;
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
    max-width: 16px;
    max-height: 16px;
    margin-right: 6px;
  }
`;



const Title = styled.h3`
  width: 100%;
  color: #222222;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 10px;
    line-height: 1.3;
    word-break: break-word;
  }
`;

const Subtitle = styled.h4`
  width: 100%;
  color: #6c757d;
  font-size: 16px;
  font-weight: 400;
  margin-top: 10px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-top: 8px;
    line-height: 1.4;
    word-break: break-word;
  }
`;

const Input = styled.input`
  line-height: 32px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  color: #495057;
  width: 100%;
  font-size: 16px;
  padding: 12px 16px;
  background: #fff;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: #222222;
    outline: none;
    box-shadow: 0 0 0 3px rgba(34, 34, 34, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 8px 12px;
    line-height: 28px;
  }
`;

const TextArea = styled.textarea`
  border: 2px solid #e9ecef;
  border-radius: 8px;
  color: #495057;
  width: 100%;
  min-height: 120px;
  font-size: 16px;
  padding: 12px 16px;
  background: #fff;
  resize: vertical;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:focus {
    border-color: #222222;
    outline: none;
    box-shadow: 0 0 0 3px rgba(34, 34, 34, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 8px 12px;
    min-height: 100px;
  }
`;


const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 12px;
  border: 1px solid #f5c6cb;
  font-size: 14px;
`;

const CheckboxWarning = styled.div`
  background: #fff9e6;
  border: 1px solid #ff8c00;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    gap: 10px;
  }
`;

const WarningIcon = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 100%;
    height: 100%;
  }
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    min-width: 20px;
  }
`;

const WarningText = styled.span`
  color: #ff8c00;
  font-weight: 600;
  font-size: 14px;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const QuestionRenderer: React.FC<QuestionRendererProps> = ({ pergunta, value, onChange, numeroDependentes = 0, usuariosQueAtendem = [], tipoUsuario = 'Titular' }) => {
  const camposExcluidos = ['nome', 'cpf', 'email', 'celular'];
  const [validationError, setValidationError] = React.useState<string>('');
  const tipoPergunta = pergunta.tipo || pergunta.template?.tipo;
  
  const deveIncluirDependentes = numeroDependentes > 0 && !camposExcluidos.some(campo => 
    pergunta.titulo.toLowerCase().includes(campo)
  );

  const validateNumberInput = React.useCallback((inputValue: string): string => {
    if (!inputValue || inputValue === '') return '';
    
    const numValue = Number(inputValue);
    
    if (isNaN(numValue)) return '';
    
    if (pergunta.tamanho_maximo && inputValue.length > pergunta.tamanho_maximo) {
      return `O valor não pode ter mais de ${pergunta.tamanho_maximo} dígitos`;
    }
    
    if (pergunta.valor_maximo !== undefined && numValue > pergunta.valor_maximo) {
      return `O valor não pode ser maior que ${pergunta.valor_maximo}`;
    }
    
    if (pergunta.valor_minimo !== undefined && numValue < pergunta.valor_minimo) {
      return `O valor não pode ser menor que ${pergunta.valor_minimo}`;
    }
    
    return '';
  }, [pergunta.tamanho_maximo, pergunta.valor_maximo, pergunta.valor_minimo]);

  React.useEffect(() => {
    if (tipoPergunta === 'number' && value) {
      const error = validateNumberInput(String(value));
      setValidationError(error);
    } else if (!value) {
      setValidationError('');
    }
  }, [value, tipoPergunta, validateNumberInput]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, usuarioIndex?: number) => {
    const inputValue = e.target.value;
    
    if (tipoPergunta === 'number') {
      setValidationError(validateNumberInput(inputValue));
    }
    
    if (numeroDependentes > 0 && usuarioIndex !== undefined) {
      const newValue = { ...value };
      if (usuarioIndex === 0) {
        newValue.titular = inputValue;
      } else {
        newValue[`dependente_${usuarioIndex}`] = inputValue;
      }
      onChange(newValue);
    } else {
      onChange(inputValue);
    }
    
    if (tipoPergunta !== 'number') {
      setValidationError('');
    }
  };

  const handleRadioChange = (optionValue: string, usuarioIndex?: number) => {
    if (numeroDependentes > 0 && usuarioIndex !== undefined) {
      const newValue = { ...value };
      if (usuarioIndex === 0) {
        newValue.titular = optionValue;
      } else {
        newValue[`dependente_${usuarioIndex}`] = optionValue;
      }
      onChange(newValue);
    } else {
      onChange(optionValue);
    }
  };

  const handleCheckboxChange = (optionValue: string, checked: boolean, usuarioIndex?: number) => {
    const isExclusivo = optionValue === 'Não' || optionValue === 'Não sei' || optionValue === 'Sim, porém nenhuma destas';
    const opcoesExclusivas = ['Não', 'Não sei', 'Sim, porém nenhuma destas'];
    
    if (numeroDependentes > 0 && usuarioIndex !== undefined) {
      const newValue = { ...value };
      const currentValues = Array.isArray(newValue[usuarioIndex === 0 ? 'titular' : `dependente_${usuarioIndex}`]) 
        ? newValue[usuarioIndex === 0 ? 'titular' : `dependente_${usuarioIndex}`] 
        : [];
      
      let updatedValues;
      
      if (checked) {
        if (isExclusivo) {
          updatedValues = [optionValue];
        } else {
          updatedValues = [...currentValues.filter((v: any) => !opcoesExclusivas.includes(v)), optionValue];
        }
      } else {
        updatedValues = currentValues.filter((v: any) => v !== optionValue);
      }
      
      if (usuarioIndex === 0) {
        newValue.titular = updatedValues;
      } else {
        newValue[`dependente_${usuarioIndex}`] = updatedValues;
      }
      onChange(newValue);
    } else {
      const currentValues = Array.isArray(value) ? value : [];
      let updatedValues;
      
      if (checked) {
        if (isExclusivo) {
          updatedValues = [optionValue];
        } else {
          updatedValues = [...currentValues.filter((v: any) => !opcoesExclusivas.includes(v)), optionValue];
        }
      } else {
        updatedValues = currentValues.filter((v: any) => v !== optionValue);
      }
      
      onChange(updatedValues);
    }
  };

  const renderDependenteFields = () => {
    if (!deveIncluirDependentes) return null;
    
    return (
      <div style={{ marginTop: '20px' }}>
        <Table>
          <Thead>
            <Tr>
              <Td>Pergunta</Td>
              {usuariosQueAtendem.includes('titular') && <Td>Titular</Td>}
              {Array.from({ length: numeroDependentes }, (_, index) => {
                const dependenteKey = `dependente_${index + 1}`;
                return usuariosQueAtendem.includes(dependenteKey) ? (
                  <Td key={index}>Dependente {index + 1}</Td>
                ) : null;
              })}
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>{pergunta.titulo}</Td>
              {usuariosQueAtendem.includes('titular') && (
                <Td>
                  <Input
                    type="text"
                    value={value?.titular || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 0)}
                    placeholder="Resposta do titular"
                  />
                </Td>
              )}
              {Array.from({ length: numeroDependentes }, (_, index) => {
                const dependenteKey = `dependente_${index + 1}`;
                return usuariosQueAtendem.includes(dependenteKey) ? (
                  <Td key={index}>
                    <Input
                      type="text"
                      value={value?.[`dependente_${index + 1}`] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, index + 1)}
                      placeholder={`Dependente ${index + 1}`}
                    />
                  </Td>
                ) : null;
              })}
            </Tr>
          </Tbody>
        </Table>
      </div>
    );
  };

  const renderQuestionWithDependents = (inputType: 'radio' | 'checkbox') => {
    if (!deveIncluirDependentes) {
      return null;
    }

    return (
      <div style={{ marginTop: '20px' }}>
        <Table>
          <Thead>
            <Tr>
              <Td>Pergunta</Td>
              {usuariosQueAtendem.includes('titular') && <Td>Titular</Td>}
              {Array.from({ length: numeroDependentes }, (_, index) => {
                const dependenteKey = `dependente_${index + 1}`;
                return usuariosQueAtendem.includes(dependenteKey) ? (
                  <Td key={index}>Dependente {index + 1}</Td>
                ) : null;
              })}
            </Tr>
          </Thead>
          <Tbody>
            {pergunta.opcoes_resposta?.map((opcao, opcaoIndex) => (
              <Tr key={opcaoIndex}>
                <Td>{opcao}</Td>
                {usuariosQueAtendem.includes('titular') && (
                  <Td>
                    <Option
                      type={inputType}
                      name={`pergunta_${pergunta.id}_titular`}
                      checked={inputType === 'radio' ? value?.titular === opcao : (Array.isArray(value?.titular) && value.titular.includes(opcao))}
                      onChange={() => {
                        if (inputType === 'radio') {
                          handleRadioChange(opcao, 0);
                        } else {
                          handleCheckboxChange(opcao, !(Array.isArray(value?.titular) && value.titular.includes(opcao)), 0);
                        }
                      }}
                    />
                  </Td>
                )}
                {Array.from({ length: numeroDependentes }, (_, depIndex) => {
                  const dependenteKey = `dependente_${depIndex + 1}`;
                  return usuariosQueAtendem.includes(dependenteKey) ? (
                    <Td key={depIndex}>
                      <Option
                        type={inputType}
                        name={`pergunta_${pergunta.id}_dependente_${depIndex + 1}`}
                        checked={inputType === 'radio' 
                          ? value?.[`dependente_${depIndex + 1}`] === opcao
                          : (Array.isArray(value?.[`dependente_${depIndex + 1}`]) && value[`dependente_${depIndex + 1}`].includes(opcao))
                        }
                        onChange={() => {
                          if (inputType === 'radio') {
                            handleRadioChange(opcao, depIndex + 1);
                          } else {
                            handleCheckboxChange(opcao, !(Array.isArray(value?.[`dependente_${depIndex + 1}`]) && value[`dependente_${depIndex + 1}`].includes(opcao)), depIndex + 1);
                          }
                        }}
                      />
                    </Td>
                  ) : null;
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    );
  };

  const renderQuestion = () => {
    switch (tipoPergunta) {
      case 'subtitulo':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
          </ContainerQuestion>
        );

      case 'text':
      case 'texto':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            <Input
              type="text"
              value={value || ''}
              onChange={handleInputChange}
              placeholder={pergunta.placeholder || ''}
              required={pergunta.obrigatorio}
              maxLength={pergunta.tamanho_maximo || undefined}
              minLength={pergunta.tamanho_minimo || undefined}
            />
            {renderDependenteFields()}
          </ContainerQuestion>
        );

      case 'tel':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            <Input
              type="tel"
              value={value || ''}
              onChange={handleInputChange}
              placeholder={pergunta.placeholder || ''}
              required={pergunta.obrigatorio}
              maxLength={pergunta.tamanho_maximo || undefined}
              minLength={pergunta.tamanho_minimo || undefined}
            />
          </ContainerQuestion>
        );

      case 'textarea':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            <TextArea
              value={value || ''}
              onChange={handleInputChange}
              placeholder={pergunta.placeholder || ''}
              required={pergunta.obrigatorio}
              maxLength={pergunta.tamanho_maximo || undefined}
              minLength={pergunta.tamanho_minimo || undefined}
            />
          </ContainerQuestion>
        );

      case 'number':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            {numeroDependentes === 0 && (
              <Input
                type="number"
                value={value || ''}
                onChange={handleInputChange}
                placeholder={pergunta.placeholder || ''}
                required={pergunta.obrigatorio}
                maxLength={pergunta.tamanho_maximo || undefined}
                minLength={pergunta.tamanho_minimo || undefined}
              />
            )}
            {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
            {renderDependenteFields()}
          </ContainerQuestion>
        );

      case 'email':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            <Input
              type="email"
              value={value || ''}
              onChange={handleInputChange}
              placeholder={pergunta.placeholder || ''}
              required={pergunta.obrigatorio}
              maxLength={pergunta.tamanho_maximo || undefined}
              minLength={pergunta.tamanho_minimo || undefined}
            />
          </ContainerQuestion>
        );

      case 'date':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            <Input
              type="date"
              value={value || ''}
              onChange={handleInputChange}
              required={pergunta.obrigatorio}
            />
          </ContainerQuestion>
        );

      case 'month':
        const isDataParto = pergunta.titulo?.toLowerCase().includes('parto');
        const minDate = isDataParto ? new Date().toISOString().slice(0, 7) : undefined;
        
        const maxDate = isDataParto ? (() => {
          const hoje = new Date();
          const dataMaxima = new Date(hoje);
          dataMaxima.setDate(hoje.getDate() + 280);
          return dataMaxima.toISOString().slice(0, 7);
        })() : undefined;
        
        const isDataAnterior = isDataParto && value && minDate && value < minDate;
        
        const isDataMuitoFutura = isDataParto && value && maxDate && value > maxDate;
        
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            <Input
              type="month"
              value={value || ''}
              onChange={handleInputChange}
              required={pergunta.obrigatorio}
              min={minDate}
              max={maxDate}
            />
            {isDataAnterior && (
              <div style={{
                background: '#f8d7da',
                color: '#721c24',
                padding: '12px 16px',
                borderRadius: '8px',
                marginTop: '12px',
                border: '1px solid #f5c6cb',
                fontSize: '14px'
              }}>
                A data não pode ser anterior ao mês atual
              </div>
            )}
            {isDataMuitoFutura && (
              <div style={{
                background: '#f8d7da',
                color: '#721c24',
                padding: '12px 16px',
                borderRadius: '8px',
                marginTop: '12px',
                border: '1px solid #f5c6cb',
                fontSize: '14px'
              }}>
                A data não pode ser maior que 280 dias
              </div>
            )}
          </ContainerQuestion>
        );

      case 'radio':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            {deveIncluirDependentes ? (
              renderQuestionWithDependents('radio')
            ) : (
              pergunta.layout === 'horizontal' ? (
                <OptionGroup>
                  <OptionContent>
                    <OptionTitle>Pergunta</OptionTitle>
                    {pergunta.opcoes_resposta?.map((opcao, index) => (
                      <OptionRow key={index}>{opcao}</OptionRow>
                    ))}
                  </OptionContent>
                  <AnswersContent>
                    <OptionContent>
                      <OptionTitle>Respostas</OptionTitle>
                      {pergunta.opcoes_resposta?.map((opcao, index) => (
                        <OptionRow key={index}>
                          <Option
                            type="radio"
                            name={`pergunta_${pergunta.id}`}
                            checked={value === opcao}
                            onChange={() => handleRadioChange(opcao)}
                          />
                        </OptionRow>
                      ))}
                    </OptionContent>
                  </AnswersContent>
                </OptionGroup>
              ) : (
                <Table>
                  <Thead>
                    <Tr>
                      <Td>Beneficiário</Td>
                      {pergunta.opcoes_resposta?.map((opcao, index) => (
                        <Td key={index}>{opcao}</Td>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <TdCentered>{tipoUsuario}</TdCentered>
                      {pergunta.opcoes_resposta?.map((opcao, index) => (
                        <Td key={index}>
                          <Option
                            type="radio"
                            name={`pergunta_${pergunta.id}`}
                            checked={value === opcao}
                            onChange={() => handleRadioChange(opcao)}
                          />
                        </Td>
                      ))}
                    </Tr>
                  </Tbody>
                </Table>
              )
            )}
          </ContainerQuestion>
        );

      case 'checkbox':
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            {pergunta.ajuda_texto && (
              <Subtitle>{pergunta.ajuda_texto}</Subtitle>
            )}
            <CheckboxWarning>
              <WarningIcon>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 22H22L12 2Z" stroke="#ff8c00" strokeWidth="2" fill="none"/>
                  <path d="M12 8V14" stroke="#ff8c00" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="12" cy="17" r="1" fill="#ff8c00"/>
                </svg>
              </WarningIcon>
              <WarningText>É possível selecionar mais de uma opção</WarningText>
            </CheckboxWarning>
            {deveIncluirDependentes ? (
              renderQuestionWithDependents('checkbox')
            ) : (
              pergunta.layout === 'horizontal' ? (
                <OptionGroup>
                  <OptionContent>
                    <OptionTitle>Pergunta</OptionTitle>
                    {pergunta.opcoes_resposta?.map((opcao, index) => (
                      <OptionRow key={index}>{opcao}</OptionRow>
                    ))}
                  </OptionContent>
                  <AnswersContent>
                    <OptionContent>
                      <OptionTitle>Respostas</OptionTitle>
                      {pergunta.opcoes_resposta?.map((opcao, index) => (
                        <OptionRow key={index}>
                          <Option
                            type="checkbox"
                            checked={Array.isArray(value) && value.includes(opcao)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(opcao, e.target.checked)}
                          />
                        </OptionRow>
                      ))}
                    </OptionContent>
                  </AnswersContent>
                </OptionGroup>
              ) : (
                <Table>
                  <Thead>
                    <Tr>
                      <Td>Pergunta</Td>
                      {pergunta.opcoes_resposta?.map((opcao, index) => (
                        <Td key={index}>{opcao}</Td>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>{pergunta.titulo}</Td>
                      {pergunta.opcoes_resposta?.map((opcao, index) => (
                        <Td key={index}>
                          <Option
                            type="checkbox"
                            checked={Array.isArray(value) && value.includes(opcao)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(opcao, e.target.checked)}
                          />
                        </Td>
                      ))}
                    </Tr>
                  </Tbody>
                </Table>
              )
            )}
          </ContainerQuestion>
        );

      default:
        return (
          <ContainerQuestion>
            <Title>{pergunta.titulo}</Title>
            <Subtitle>Tipo de pergunta não suportado: {tipoPergunta || 'não informado'}</Subtitle>
          </ContainerQuestion>
        );
    }
  };

  return (
    <div data-pergunta-id={pergunta.id}>
      {renderQuestion()}
    </div>
  );
};

export default QuestionRenderer;